import { computed, ref, watch, WritableComputedRef } from 'vue';
import { openBrowserPopup } from '@/common/utils/browserPopupUtils';
import { POPUP_MIN_SIZE } from '@/common/components/organisms/alertListDetail/alertListDetail.setup';
import { useDashboardViewStore } from '@/dashboard/stores/dashboard-view';
import { useRequestTags } from '@/dashboard/features/widgetsAPI/composables/useRequestTags';
import { webStorageController } from '@/common/utils/browser';
import { useInternational } from '@/common/locale';
import { useInstanceIds } from '@/database/utils/alert.uses';
import { USE_SLIDE_VIEW } from '@/common/components/organisms/alertListDetail/alertListDetail.define';
import { AlertInfoType } from '@/common/components/organisms/overviewAlert/overviewAlert.setup';
import { useDashboardWidgetsAlertStore } from '@/dashboard/stores/dashboard-widgets-alert';
import { TagRequest } from '@/api-client/metav6/model';
import type { AlertGroupCountRequestPlatformTypesItem } from '@/api-client/alert/model';
import { WidgetUUID } from '@/common/utils';
import { SlideViewType } from './alertListDetail.types';

export interface SlideFilter {
  platformTypes: AlertGroupCountRequestPlatformTypesItem[];
  targetIds: string[];
  dataIds: string[];
  fieldNames: string[];
}
export interface Props {
  isVisible: boolean;
  type: SlideViewType;
  targetIds?: string[];
  alertInfo?: AlertInfoType;
  filter?: SlideFilter;
}
export interface Emit {
  (e: 'update:is-visible', value: boolean): void;
  (e: 'openDetail'): void;
}

const useDashboardParams = (isShow: WritableComputedRef<boolean>) => {
  const monitoringDashboard = computed(() => useDashboardViewStore().monitoringDashboard);
  const { getRequestUserTags } = useRequestTags();

  const setAlertDetailParams = (dashboardParams: {
    dashboardId: number;
    userTags: TagRequest[];
    widgetField?: { widgetUUId?: WidgetUUID; fieldNames?: string[] };
  }) => {
    webStorageController.setItem({
      type: 'session',
      key: 'alertDetailParams',
      value: JSON.stringify({
        dashboard: dashboardParams,
      }),
    });
  };

  watch(
    [() => monitoringDashboard.value.dashboardId, getRequestUserTags],
    ([dashboardId, userTags]) => {
      isShow.value = false;
      setAlertDetailParams({ dashboardId, userTags });
    },
  );

  const clickedAlertWidgetInfo = computed(
    () => useDashboardWidgetsAlertStore().clickedAlertWidgetInfo,
  );

  watch(
    () => clickedAlertWidgetInfo.value,
    (widgetInfo) => {
      const { dashboardId } = monitoringDashboard.value;
      const userTags = getRequestUserTags();
      setAlertDetailParams({
        dashboardId,
        userTags,
        ...(widgetInfo.widgetId && {
          widgetField: { widgetUUId: widgetInfo.widgetId, fieldNames: widgetInfo.fieldNames },
        }),
      });
    },
  );
};
const useDatabaseParams = (isShow: WritableComputedRef<boolean>) => {
  const { currentRouteInfo, getInstanceIds } = useInstanceIds();
  const instanceIds = computed(() => getInstanceIds());
  watch(
    [currentRouteInfo, instanceIds],
    ([currRoute, ids]) => {
      if (ids) {
        isShow.value = false;

        const { view, dbType } = currRoute;
        webStorageController.setItem({
          type: 'session',
          key: 'alertDetailParams',
          value: JSON.stringify({ database: { instanceIds: getInstanceIds(), view, dbType } }),
        });
      }
    },
    { deep: true, immediate: true },
  );
};

// NOTE: 화면별 type을 기준으로 셋팅하는 로직입니다
const usePropsTargetSubKind = (props: Props, isShow: WritableComputedRef<boolean>) => {
  watch(
    () => props.type,
    () => {
      if (props.type) {
        webStorageController.setItem({
          type: 'session',
          key: 'alertDetailParams',
          value: JSON.stringify({
            [props.type]: {
              alertInfo: props.alertInfo,
            },
          }),
        });
      }
      isShow.value = false;
    },
    { immediate: true },
  );
};

const useDefineParams = {
  [USE_SLIDE_VIEW.DASHBOARD]: useDashboardParams,
  [USE_SLIDE_VIEW.DATABASE]: useDatabaseParams,
};

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();

  const isShow = computed({
    get: () => props.isVisible,
    set: (value) => emit('update:is-visible', value),
  });

  const slideWidth = ref('900px');
  const onExpandSlide = (isExpand: boolean) => {
    slideWidth.value = isExpand ? '1200px' : '900px';
  };
  const onClickOpenPopup = () => {
    // 팝업은 props(filter/alertInfo)를 직접 받지 못하고 세션(alertDetailParams)에서 복원하므로,
    // popout 직전 '현재' 값을 세션에 확정 적재한다. (usePropsTargetSubKind 는 props.type 변경 시에만 기록해서
    // 이후 filter 갱신을 반영 못 함 → 팝업이 전체 알람을 조회하던 문제 보완). dashboard/database 는 자체 setter 가 관리.
    if (props.type && !useDefineParams[props.type]) {
      webStorageController.setItem({
        type: 'session',
        key: 'alertDetailParams',
        value: JSON.stringify({
          [props.type]: {
            alertInfo: props.alertInfo,
            filter: props.filter,
          },
        }),
      });
    }
    openBrowserPopup({
      query: {
        viewName: 'alertListDetail',
        isShow: true,
        type: props.type,
      },
      popupStyle: { width: POPUP_MIN_SIZE.WIDTH, height: POPUP_MIN_SIZE.HEIGHT },
      isSingle: true,
      sharedStorageInfo: {
        session: ['alertDetailParams'],
      },
    });
  };

  const clickedAlertWidgetInfo = computed(
    () => useDashboardWidgetsAlertStore().clickedAlertWidgetInfo,
  );

  const slideDetailTitle = computed(() => {
    if (props.type !== USE_SLIDE_VIEW.DASHBOARD) return t('WORD.ALERT');
    const { widgetName } = clickedAlertWidgetInfo.value;
    return widgetName ? `${t('WORD.ALERT')}: ${widgetName}` : t('WORD.ALERT');
  });

  watch(
    () => props.type,
    (type) => {
      if (useDefineParams[type]) {
        useDefineParams[type](isShow);
      } else {
        usePropsTargetSubKind(props, isShow);
      }
    },
    { immediate: true },
  );

  return {
    isShow,
    slideWidth,
    slideDetailTitle,
    onExpandSlide,
    onClickOpenPopup,
  };
};
